import {api} from '@/shared/services/api-client';
import {SCHEDULES_LISTALL_PAGE_SIZE} from '@/config/pagination';
import {
  paginateAll,
  reportTruncationIfAny,
} from '@/shared/services/paginateAll';

/**
 * Shared schedule retrieval layer.
 *
 * Wraps the backend `GET /schedules` endpoint with:
 *  - a typed parameter set (no more `(... as any)` calls),
 *  - silent-truncation telemetry (warns when the server reports more rows than it
 *    returned, which is exactly the bug large hardcoded limits caused),
 *  - a `fetchAllSchedules` paginate-all loop for export / full-set computations,
 *    modeled on `participantsApi.listAll`.
 *
 * Screens that display a list should call `fetchSchedulesPage` with `page` +
 * `limit`. Screens that genuinely need every row (export, eligibility, conflict
 * windows that can't be date-scoped) should call `fetchAllSchedules`.
 */

export interface ScheduleListParams {
  project_id?: string;
  participant_id?: string;
  moderator_id?: string;
  status?: string;
  meeting_type?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** Single id or comma-separated list of session type ids. */
  session_type_id?: string;
  /** 'upcoming' | 'past' relative to now (by end_time). */
  time_filter?: 'upcoming' | 'past';
  /** Payment Approvals: only schedules carrying a payment record. */
  has_payment?: boolean;
  /** Payment Approvals: bucket on payment_status. */
  payment_approval?: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'rejected_cancelled';
  /** Payment Approvals / Action Center: only sessions flagged for bonus/partial-payment review (OD-1070/OD-1071). */
  bonus_flag?: boolean;
  /**
   * Payment Approvals: narrow the flagged requests to one queue — a bonus a
   * Moderator/Scheduler asked for, or a partial payment owed for a session
   * cancelled/rescheduled with payment. Implies `bonus_flag`.
   */
  payment_request?: 'bonus' | 'partial_payment';
  /**
   * Payment Approvals: filter on oneforma_payment_status (independent of
   * payment_approval). Value set is DB-driven (see payment-status-options),
   * not a fixed enum.
   */
  oneforma_payment_status?: string;
  /** Payment Approvals: marks a PM-only request used to build a CSV export. */
  export_data?: boolean;
  /**
   * Payment Approvals: only sessions whose captured actual times a PM/moderator
   * manually overrode (OD-1246). Backed by `schedules.time_adjusted`, which is
   * set solely by the manual adjustment endpoint — it is not derivable from
   * `actual_start_time`/`actual_end_time` being present, since those are also
   * written by the automatic In Progress / Completed / check-in capture.
   */
  time_adjusted?: boolean;
  page?: number;
  limit?: number;
}

export interface SchedulePagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ScheduleListData {
  schedules: any[];
  pagination?: SchedulePagination;
}

export interface ScheduleListResponse {
  data: ScheduleListData;
}

const ROWS_KEY = 'schedules';

/**
 * Fetch a single page of schedules. Same response shape as the raw API call, so
 * this is a drop-in for existing `schedulesApi.listSchedules(...)` callers, but
 * it adds truncation telemetry. Pass `context` to label the warning source.
 */
export const fetchSchedulesPage = async (
  params?: ScheduleListParams,
  context = 'fetchSchedulesPage',
): Promise<ScheduleListResponse> => {
  const res: any = await api.schedules.list(params);
  reportTruncationIfAny(res?.data, ROWS_KEY, context);
  return res as ScheduleListResponse;
};

/**
 * Fetch *every* schedule matching `params` by paging through the backend in
 * `SCHEDULES_LISTALL_PAGE_SIZE` chunks (via the shared `paginateAll` helper).
 * Returns the same `{ data: { schedules, pagination } }` shape as a single page
 * so callers can drop it in where they used a single large `limit`.
 */
export const fetchAllSchedules = async (
  params?: ScheduleListParams,
  pageSize: number = SCHEDULES_LISTALL_PAGE_SIZE,
): Promise<ScheduleListResponse> => {
  const {rows, pagination} = await paginateAll(
    (p) => api.schedules.list(p),
    {...params},
    ROWS_KEY,
    pageSize,
  );
  return {data: {schedules: rows, pagination}};
};

/**
 * Adapter from the generic `useServerPagination` request shape to a single
 * server-side page of schedules. `baseParams` carries screen-level scoping +
 * filters (project_id, status[], session_type_id[], location[], moderator_id[],
 * time_filter, date range). Returns `{ rows, total, pages }`.
 */
export const fetchSchedulesServerPage = async (
  req: {
    page: number;
    limit: number;
    search: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters: Record<string, unknown>;
  },
  baseParams: ScheduleListParams = {},
): Promise<{rows: any[]; total: number; pages: number}> => {
  const params: ScheduleListParams = {
    ...baseParams,
    ...(req.filters as ScheduleListParams),
    page: req.page,
    limit: req.limit,
    search: req.search || undefined,
    sort_by: req.sortBy,
    sort_dir: req.sortDir,
  };
  const res = await fetchSchedulesPage(params, 'fetchSchedulesServerPage');
  const data = res.data ?? {};
  return {
    rows: data.schedules ?? [],
    total: data.pagination?.total ?? 0,
    pages: data.pagination?.pages ?? 0,
  };
};

export interface ScheduleStatusCount {
  status: string | null;
  count: number;
}

/** Fetch aggregate status counts for the KPI cards (no row payload). */
export const fetchScheduleStatusCounts = async (
  projectId?: string,
): Promise<{total: number; by_status: ScheduleStatusCount[]}> => {
  const res: any = await api.schedules.statusCounts(
    projectId ? {project_id: projectId} : undefined,
  );
  const data = res?.data ?? {};
  return {total: data.total ?? 0, by_status: data.by_status ?? []};
};
